import React from 'react';
import PropTypes from 'prop-types';
import STRINGS from 'constants/strings';
import PriceRange from 'components/price-range/price-range';
import { Accordion, AccordionItem } from 'tradera-components/accordion';
import LoadingIcon from '@tradera/blueprint/theme/icons2/loading.svg?jsx';
import { SortRadioButtons } from '../search-results/sort/sort-radio-buttons';
import { AttributeFilter } from './attributes/attribute-filter';
import { getTranslatedAttributeName } from 'tradera-components/attributes/attribute-helper';
import { formatNumber } from 'packages';
import CONSTANTS from '../../constants/constants';
import ItemLayoutSwitch from '../search-results/layout/item-layout-switch';
import { RadioSwitchFilter } from './radio-switch-filter';
import { SelectFilter } from './select-filter';
import { useTranslator } from 'tradera-lang/translate';
import * as gtm from '../../helpers/search-gtm';
import { getTotalActiveFilterCount } from '../../helpers/filter';
import { getAttributeIcon } from './icon-helper';
import ItemTypeIcon from '@tradera/blueprint/theme/icons2/auction.svg?jsx';
import ItemStatusIcon from '@tradera/blueprint/theme/icons2/bids.svg?jsx';
import SellerTypeIcon from '@tradera/blueprint/theme/icons2/user.svg?jsx';
import CountyIcon from '@tradera/blueprint/theme/icons2/location.svg?jsx';
import ItemConditionIcon from '@tradera/blueprint/theme/icons2/condition.svg?jsx';
import PriceIcon from '@tradera/blueprint/theme/icons2/price.svg?jsx';

const getSelectedValue = (filter = {}) => {
    const { selectedValue } = filter;
    if (!selectedValue) {
        return '';
    }
    const selected = filter.options.find(option => {
        return option.value === selectedValue;
    });
    const activeFilter = CONSTANTS.ACTIVE_FILTER_STATES.includes(
        selected.value
    );
    return (
        <span
            className={`${
                activeFilter ? 'text-inter-medium text-primary' : ''
            } mr-3`}>
            {selected.name}
        </span>
    );
};

const SearchFilterModal = ({
    activeFilters,
    attributeFilters = [],
    attributeFiltersEnabled,
    filterAccordionOpen,
    filterPrice = {},
    filters,
    handleAttributeFilterChange,
    handleClearAllFilters,
    handleFilterChange,
    handlePriceFilterChange,
    isLoading,
    pageType,
    queryParams,
    totalItems
}) => {
    const { t } = useTranslator();
    const {
        itemCondition,
        sellerType,
        itemStatus,
        county,
        itemType,
        sortBy
    } = filters;
    const { fromPrice, toPrice } = filterPrice;

    const totalActiveFilters = getTotalActiveFilterCount(
        fromPrice,
        toPrice,
        attributeFilters,
        activeFilters
    );

    return (
        <>
            <div className="search-modal__header d-flex justify-content-center align-items-center">
                <strong>{t('searchFiltersModal_title')}</strong>
                <button
                    className="clear-all--modal btn btn-outline-danger"
                    onClick={() => {
                        gtm.trackAction('Filter menu', 'Reset');
                        handleClearAllFilters();
                    }}>
                    {t('searchFilters_clear')}
                    {totalActiveFilters > 0 && (
                        <span className="d-inline-block ml-2">
                            ({totalActiveFilters})
                        </span>
                    )}
                </button>
            </div>
            <div className="search-modal__hits d-flex align-items-center justify-content-center unbutton">
                <span>
                    {t('searchFilters_itemsFound', {
                        itemsCount: formatNumber(totalItems),
                        count: totalItems
                    })}
                </span>
            </div>
            {isLoading && (
                <div className="search-filter__loading d-flex bg-white">
                    <LoadingIcon className="icon icon-xl icon-spin m-auto" />
                </div>
            )}
            <Accordion>
                <AccordionItem
                    hideTitle={true}
                    defaultOpen
                    collapsable={false}
                    render={() => (
                        <div className="d-flex justify-content-between py-2">
                            <span className="align-self-center mr-4 ml-1 text-styled">
                                {t('searchFilters_layout')}:
                            </span>
                            <ItemLayoutSwitch
                                className={'flex-grow-1'}
                                id="filter-modal-itemLayout"
                            />
                        </div>
                    )}
                />
                <AccordionItem
                    title={t('searchFilters_sortTitle')}
                    onChange={() => gtm.trackAccordion('sortBy')}
                    defaultOpen={
                        filterAccordionOpen === STRINGS.DISCOVER.FILTERS.SORTING
                    }
                    label={getSelectedValue(sortBy)}
                    render={() => (
                        <SortRadioButtons
                            sortBy={sortBy}
                            handleFilterChange={handleFilterChange}
                            id="filter-modal-sortBy"
                        />
                    )}
                />
                {attributeFiltersEnabled &&
                    attributeFilters.map(attributeFilter => (
                        <AccordionItem
                            key={attributeFilter.parameter}
                            title={getTranslatedAttributeName(
                                attributeFilter.parameter,
                                t
                            )}
                            onChange={() =>
                                gtm.trackAccordion(attributeFilter.parameter)
                            }
                            defaultOpen={
                                filterAccordionOpen ===
                                attributeFilter.parameter
                            }
                            label={
                                attributeFilter.selectedValues.length > 0 && (
                                    <span className="badge badge-pill-round badge-primary mr-3">
                                        {attributeFilter.selectedValues.length}
                                    </span>
                                )
                            }
                            icon={getAttributeIcon(attributeFilter.parameter)}
                            render={() => (
                                <AttributeFilter
                                    attributeFilter={attributeFilter}
                                    handleAttributeFilterChange={
                                        handleAttributeFilterChange
                                    }
                                    queryParams={queryParams}
                                    pageType={pageType}
                                />
                            )}
                        />
                    ))}

                <AccordionItem
                    title={t('searchFilters_itemStatusTitle')}
                    onChange={() => gtm.trackAccordion('itemStatus')}
                    defaultOpen={
                        filterAccordionOpen ===
                        STRINGS.DISCOVER.FILTERS.ITEM_STATUS
                    }
                    label={getSelectedValue(itemStatus)}
                    icon={<ItemStatusIcon className="icon icon-md" />}
                    render={() => (
                        <RadioSwitchFilter
                            filter={itemStatus}
                            handleFilterChange={handleFilterChange}
                            id="filter-modal-itemStatus"
                        />
                    )}
                />
                <AccordionItem
                    title={t('searchFilters_priceTitle')}
                    onChange={() => gtm.trackAccordion('price')}
                    collapsable={false}
                    defaultOpen
                    icon={<PriceIcon className="icon icon-md" />}
                    render={() => (
                        <PriceRange
                            rangeStart={1}
                            rangeEnd={5000}
                            handleStart={filterPrice['fromPrice'].value}
                            handleEnd={filterPrice['toPrice'].value}
                            handlePriceFilterChange={handlePriceFilterChange}
                        />
                    )}
                />
                <AccordionItem
                    title={t('searchFilters_itemTypeTitle')}
                    onChange={() => gtm.trackAccordion('itemType')}
                    defaultOpen={
                        filterAccordionOpen ===
                        STRINGS.DISCOVER.FILTERS.ITEM_TYPE
                    }
                    label={getSelectedValue(itemType)}
                    icon={<ItemTypeIcon className="icon icon-md" />}
                    render={() => (
                        <RadioSwitchFilter
                            filter={itemType}
                            handleFilterChange={handleFilterChange}
                            id="filter-modal-itemType"
                        />
                    )}
                />
                <AccordionItem
                    title={t('searchFilters_conditionTitle')}
                    onChange={() => gtm.trackAccordion('itemCondition')}
                    defaultOpen={
                        filterAccordionOpen ===
                        STRINGS.DISCOVER.FILTERS.CONDITION
                    }
                    label={getSelectedValue(itemCondition)}
                    icon={<ItemConditionIcon className="icon icon-md" />}
                    render={() => (
                        <RadioSwitchFilter
                            filter={itemCondition}
                            handleFilterChange={handleFilterChange}
                            id="filter-modal-itemCondition"
                        />
                    )}
                />
                <AccordionItem
                    title={t('searchFilters_sellerTypeTitle')}
                    onChange={() => gtm.trackAccordion('sellerType')}
                    defaultOpen={
                        filterAccordionOpen ===
                        STRINGS.DISCOVER.FILTERS.SELLER_TYPE
                    }
                    label={getSelectedValue(sellerType)}
                    icon={<SellerTypeIcon className="icon icon-md" />}
                    render={() => (
                        <RadioSwitchFilter
                            filter={sellerType}
                            handleFilterChange={handleFilterChange}
                            id="filter-modal-sellerType"
                        />
                    )}
                />
                <AccordionItem
                    title={t('searchFilters_countyTitle')}
                    onChange={() => gtm.trackAccordion('county')}
                    defaultOpen={
                        filterAccordionOpen === STRINGS.DISCOVER.FILTERS.COUNTY
                    }
                    label={getSelectedValue(county)}
                    icon={<CountyIcon className="icon icon-md" />}
                    render={() => (
                        <SelectFilter
                            filter={county}
                            handleFilterChange={handleFilterChange}
                            id="filter-countyBox"
                        />
                    )}
                />
            </Accordion>
        </>
    );
};

SearchFilterModal.propTypes = {
    attributeFiltersEnabled: PropTypes.bool,
    filterAccordionOpen: PropTypes.string,
    handleAttributeFilterChange: PropTypes.func,
    handleClearAllFilters: PropTypes.func,
    handleFilterChange: PropTypes.func,
    handlePriceFilterChange: PropTypes.func,
    isLoading: PropTypes.bool,
    pageType: PropTypes.string,
    queryParams: PropTypes.object
};

export default SearchFilterModal;
